import { maxWeightMatching, type WeightedEdge } from '../matching/blossom.js';
import { isPlayedResult, pointsFor, type Colour, type Score } from '../model/types.js';
import {
  allocateColoursWithRule,
  colourState,
  type ColourRule,
  type ColourState,
} from './colour.js';
import { buildDutchPlayers, type DutchPlayer } from './dutch/state.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingPlayer,
  type PairingRequest,
  type PairingResult,
} from './types.js';

/**
 * FIDE Dubov System, C.04.4.1 as applied from 1 February 2026.
 *
 * Brackets are paired from the top scoregroup down. Every resident is paired
 * inside its bracket; the bracket is completed with upfloaters from lower
 * scoregroups (there are no downfloaters). Inside a bracket the White seekers
 * (G1, sorted by ascending ARO) meet the Black seekers (G2) in the first
 * legal transposition of G2 by TPN, so players with a low ARO meet
 * opponents with high ratings.
 *
 * Method:
 * - The set of upfloaters (3.2.1-3.2.2, [C4]-[C10], then the set order of
 *   4.2) comes from one maximum-weight matching over the residents and every
 *   lower player, with the criteria packed into the edge weights.
 * - The shifts between G1 and G2 (3.2.4) take the first set in the order of
 *   4.3 for which a legal pairing with the best [C7] exists; each candidate
 *   is tested with a matching.
 * - T2 (3.2.6) is the lexicographically first transposition, found by a
 *   matching whose weights rank S1's first player's opponent highest.
 *
 * Interpretations (the text leaves these open):
 * - [C7] for a candidate set of upfloaters is the best achievable in the
 *   bracket (the procedure reaches it except in rare mixed-shift cases).
 * - [C9] counts, over the maximum upfloaters chosen, the upfloats they
 *   already have (fewer is better).
 * - Shifter pools are sorted with the rule of the group they leave: from G1
 *   by ascending ARO then TPN (White seekers), from G2 by TPN (Black
 *   seekers). Players shifted by 3.2.4.1 are not shifted back by 3.2.4.2.
 * - A past game counts as an upfloat when the opponent had a higher score
 *   (including acceleration points) before that round.
 */

export const DUBOV_COLOUR_RULE: Record<ColourRule, { article: string; text: string }> = {
  '5.2.5': { article: '5.2.1', text: 'neither has played: initial colour by TPN parity' },
  '5.2.1': { article: '5.2.2', text: 'both colour preferences granted' },
  '5.2.2': { article: '5.2.3', text: 'the stronger colour preference granted' },
  '5.2.3': { article: '5.2.4', text: 'colours alternated from the last round they differed' },
  '5.2.4': { article: '5.2.5', text: "the higher-ranked player's preference granted" },
};

/** Maximum number of upfloats before a player is a maximum upfloater (1.8.2). */
export function dubovMaxUpfloats(totalRounds: number): number {
  return 2 + Math.floor(totalRounds / 5);
}

/** Average rating of over-the-board opponents, rounded half up; 0 with no games (1.7). */
export function averageRatingOfOpponents(request: PairingRequest): Map<number, number> {
  const rating = new Map(request.players.map((p) => [p.id, p.rating]));
  const out = new Map<number, number>();
  for (const p of request.players) {
    let sum = 0;
    let n = 0;
    for (const e of p.history.slice(0, request.round - 1)) {
      if (!isPlayedResult(e.result) || e.opponent === null) continue;
      sum += rating.get(e.opponent) ?? 0;
      n++;
    }
    out.set(p.id, n === 0 ? 0 : Math.floor(sum / n + 0.5));
  }
  return out;
}

/** Upfloats so far, and whether the player upfloated in the previous round. */
export function dubovUpfloats(
  request: PairingRequest,
): Map<number, { count: number; last: boolean }> {
  const ps = request.pointSystem;
  const byId = new Map(request.players.map((p) => [p.id, p]));
  const scoreBefore = (p: PairingPlayer, round: number): Score => {
    let s = 0;
    for (let r = 0; r < round - 1 && r < p.history.length; r++) {
      s += pointsFor((p.history[r] as PairingPlayer['history'][number]).result, ps);
    }
    return s + (p.accelerationHistory?.[round - 1] ?? 0);
  };
  const out = new Map<number, { count: number; last: boolean }>();
  const played = request.round - 1;
  for (const p of request.players) {
    let count = 0;
    let last = false;
    for (let r = 1; r <= played && r <= p.history.length; r++) {
      const e = p.history[r - 1] as PairingPlayer['history'][number];
      if (!isPlayedResult(e.result) || e.opponent === null) continue;
      const opp = byId.get(e.opponent);
      if (opp === undefined) continue;
      if (scoreBefore(opp, r) > scoreBefore(p, r)) {
        count++;
        if (r === played) last = true;
      }
    }
    out.set(p.id, { count, last });
  }
  return out;
}

/** Sequence numbers from the middle of a sorted list outwards (4.3.3). */
export function middleFirst<T>(sorted: readonly T[]): T[] {
  const rest = [...sorted];
  const out: T[] = [];
  while (rest.length > 0) {
    const i = rest.length % 2 === 1 ? (rest.length - 1) / 2 : rest.length / 2 - 1;
    out.push(rest.splice(i, 1)[0] as T);
  }
  return out;
}

const bitsFor = (x: bigint): bigint => {
  let b = 0n;
  let v = x;
  while (v > 0n) {
    v >>= 1n;
    b++;
  }
  return b;
};
const one = (b: boolean): bigint => (b ? 1n : 0n);

interface Field {
  readonly width: bigint;
  readonly value: (h: DutchPlayer, l: DutchPlayer) => bigint;
}

/** Dubov colour preference: players without games are mild Black seekers (1.6.4). */
const pref = (p: DutchPlayer): Colour => p.preference ?? 'b';

/** [C1] and [C3]. */
function compatible(a: DutchPlayer, b: DutchPlayer): boolean {
  if (a.cannotMeet.has(b.id) || b.cannotMeet.has(a.id)) return false;
  return !(a.strength === 'absolute' && b.strength === 'absolute' && a.preference === b.preference);
}

/** Maximum-weight matching over `vertices`; returns partner ids. */
function solve(
  vertices: readonly DutchPlayer[],
  fields: readonly Field[],
  allowed: (h: DutchPlayer, l: DutchPlayer) => boolean = () => true,
): Map<number, number> {
  const edges: WeightedEdge[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const h = vertices[i] as DutchPlayer;
    for (let j = i + 1; j < vertices.length; j++) {
      const l = vertices[j] as DutchPlayer;
      if (!compatible(h, l) || !allowed(h, l)) continue;
      let w = 0n;
      for (const f of fields) w = (w << f.width) + f.value(h, l);
      edges.push({ u: i, v: j, w });
    }
  }
  const mate = maxWeightMatching(vertices.length, edges);
  const partner = new Map<number, number>();
  for (let i = 0; i < vertices.length; i++) {
    const m = mate[i] as number;
    if (m >= 0) partner.set((vertices[i] as DutchPlayer).id, (vertices[m] as DutchPlayer).id);
  }
  return partner;
}

function completable(players: readonly DutchPlayer[]): boolean {
  if (players.length % 2 === 1) return false;
  if (players.length === 0) return true;
  return solve(players, [{ width: 1n, value: () => 1n }]).size === players.length;
}

function pairsOf(players: readonly DutchPlayer[], partner: ReadonlyMap<number, number>) {
  const byId = new Map(players.map((p) => [p.id, p]));
  const out: [DutchPlayer, DutchPlayer][] = [];
  const seen = new Set<number>();
  for (const p of players) {
    const q = partner.get(p.id);
    if (q === undefined || seen.has(p.id) || !byId.has(q)) continue;
    seen.add(p.id);
    seen.add(q);
    out.push([p, byId.get(q) as DutchPlayer]);
  }
  return out;
}

/**
 * Within-group pairs of `group` need exactly one designated (shifted)
 * member, and designated members pair only inside the group. Returns the
 * pairing quality: [perfect, within-group pairs, pairs granting colours].
 */
function shiftOracle(
  bracket: readonly DutchPlayer[],
  group: ReadonlySet<number>,
  forbidOtherOther: boolean,
  forced: ReadonlySet<number>,
  excluded: ReadonlySet<number>,
): { perfect: boolean; within: number; colours: number } {
  const n = bracket.length;
  const cw = bitsFor(BigInt(n + 2));
  const inG = (p: DutchPlayer): boolean => group.has(p.id);
  const partner = solve(
    bracket,
    [
      { width: cw, value: () => 1n },
      { width: cw, value: (h, l) => one(!(inG(h) && inG(l))) },
      { width: cw, value: (h, l) => one(pref(h) !== pref(l)) },
    ],
    (h, l) => {
      if (inG(h) && inG(l)) {
        return (
          (!excluded.has(h.id) && !forced.has(l.id)) || (!excluded.has(l.id) && !forced.has(h.id))
        );
      }
      if (inG(h)) return !forced.has(h.id);
      if (inG(l)) return !forced.has(l.id);
      return !forbidOtherOther;
    },
  );
  const pairs = pairsOf(bracket, partner);
  return {
    perfect: partner.size === n,
    within: pairs.filter(([a, b]) => inG(a) && inG(b)).length,
    colours: pairs.filter(([a, b]) => pref(a) !== pref(b)).length,
  };
}

/**
 * The first set of `k` shifters from `pool` (in sequence order, 4.1.3) that
 * allows a legal pairing with `within` within-group pairs and the best [C7].
 */
function chooseShifters(
  bracket: readonly DutchPlayer[],
  group: ReadonlySet<number>,
  forbidOtherOther: boolean,
  pool: readonly DutchPlayer[],
  k: number,
  fixedOut: ReadonlySet<number>,
): DutchPlayer[] {
  const best = shiftOracle(bracket, group, forbidOtherOther, new Set(), fixedOut);
  const ok = (forced: Set<number>, excluded: Set<number>): boolean => {
    const r = shiftOracle(bracket, group, forbidOtherOther, forced, excluded);
    return r.perfect && r.within === best.within && r.colours === best.colours;
  };
  const allIds = (ps: readonly DutchPlayer[]): Set<number> => new Set(ps.map((p) => p.id));
  const first = pool.slice(0, k);
  if (ok(allIds(first), new Set([...fixedOut, ...allIds(pool.slice(k))]))) return first;
  const forced = new Set<number>();
  const excluded = new Set<number>(fixedOut);
  const chosen: DutchPlayer[] = [];
  for (const p of pool) {
    if (chosen.length === k) {
      excluded.add(p.id);
      continue;
    }
    forced.add(p.id);
    if (ok(forced, excluded)) {
      chosen.push(p);
    } else {
      forced.delete(p.id);
      excluded.add(p.id);
    }
  }
  if (chosen.length !== k) throw new PairingError('no legal set of colour shifters (3.2.4)');
  return chosen;
}

export class DubovEngine implements PairingEngine {
  readonly id = 'dubov';
  readonly name = 'FIDE Dubov (C.04.4.1, 2026)';

  pair(request: PairingRequest): PairingResult {
    const players = buildDutchPlayers(request);
    const notes: string[] = [];
    const explanation: string[] = [];
    if (players.length === 0) return { pairs: [], byes: [], notes };
    const aro = averageRatingOfOpponents(request);
    const ups = dubovUpfloats(request);
    const maxT = dubovMaxUpfloats(request.totalRounds);
    const lastRound = request.round >= request.totalRounds;
    const fmt = (x: number): string => (x / 10).toFixed(1);
    const A = (p: DutchPlayer): number => aro.get(p.id) ?? 0;
    const upCount = (p: DutchPlayer): number => ups.get(p.id)?.count ?? 0;
    const isMax = (p: DutchPlayer): boolean => upCount(p) >= maxT;

    // Pairing-allocated bye first (1.9.2, 3.1).
    let byes: number[] = [];
    let pool = players;
    if (players.length % 2 === 1) {
      const bye = players
        .filter((p) => p.pabEligible)
        .sort((a, b) => a.score - b.score || b.playedGames - a.playedGames || b.id - a.id)
        .find((c) => completable(players.filter((p) => p !== c)));
      if (bye === undefined) {
        throw new PairingError(
          'no pairing satisfies the absolute criteria (C.04.4.1 1.9.3: the Chief Arbiter decides)',
        );
      }
      byes = [bye.id];
      pool = players.filter((p) => p !== bye);
      explanation.push(
        `Pairing-allocated bye: ${bye.id} (3.1: eligible, leaves a complete pairing, lowest score ${fmt(bye.score)}, most games, largest TPN).`,
      );
    } else if (!completable(players)) {
      throw new PairingError(
        'no pairing satisfies the absolute criteria (C.04.4.1 1.9.3: the Chief Arbiter decides)',
      );
    }

    const n = pool.length;
    const cw = bitsFor(BigInt(n + 2));
    const scores = [...new Set(pool.map((p) => p.score))].sort((a, b) => a - b);
    // Lower scores get higher bits: [C6] penalises low upfloater scores first.
    const shiftLow = new Map<number, bigint>();
    let lowWidth = 0n;
    for (const s of [...scores].reverse()) {
      shiftLow.set(s, lowWidth);
      lowWidth += bitsFor(BigInt(pool.filter((p) => p.score === s).length + 1));
    }
    const top = 1n << lowWidth;
    const maxUps = Math.max(0, ...pool.map(upCount));

    const finalPairs: [DutchPlayer, DutchPlayer][] = [];
    let remaining = [...pool].sort((a, b) => b.score - a.score || a.id - b.id);
    for (const s of [...scores].reverse()) {
      const residents = remaining.filter((p) => p.score === s);
      if (residents.length === 0) continue;
      const R = new Set(residents.map((p) => p.id));
      const lower = remaining.filter((p) => !R.has(p.id));
      const seq = new Map(lower.map((p, i) => [p.id, i]));
      const P = BigInt(lower.length);
      const rr = (_h: DutchPlayer, l: DutchPlayer): boolean => R.has(l.id);
      const rl = (h: DutchPlayer, l: DutchPlayer): boolean => R.has(h.id) && !R.has(l.id);
      const fields: Field[] = [
        // [C4] completion.
        { width: cw, value: () => 1n },
        // [C5] fewest upfloaters: most resident-resident pairs.
        { width: cw, value: (h, l) => one(rr(h, l)) },
        // [C6] upfloater scores, the lowest first.
        {
          width: lowWidth + 1n + cw,
          value: (h, l) => (rl(h, l) ? top - (1n << (shiftLow.get(l.score) as bigint)) : 0n),
        },
        // [C7] colour preferences granted in the bracket.
        {
          width: cw,
          value: (h, l) => one((rr(h, l) || rl(h, l)) && pref(h) !== pref(l)),
        },
      ];
      if (!lastRound) {
        const C = BigInt(maxUps + 1);
        fields.push(
          // [C8] fewest maximum upfloaters.
          { width: cw, value: (h, l) => one(rl(h, l) && !isMax(l)) },
          // [C9] fewest earlier upfloats among the maximum upfloaters.
          {
            width: bitsFor(C * BigInt(n + 1)),
            value: (h, l) => (rl(h, l) ? (isMax(l) ? C - BigInt(upCount(l)) : C) : 0n),
          },
          // [C10] fewest upfloaters who upfloated in the previous round.
          { width: cw, value: (h, l) => one(rl(h, l) && !(ups.get(l.id)?.last ?? false)) },
        );
      }
      // 4.2: the set with the smallest differing sequence number first.
      fields.push({
        width: P + 1n,
        value: (h, l) => (rl(h, l) ? 1n << (P - 1n - BigInt(seq.get(l.id) as number)) : 0n),
      });
      const partner = solve(remaining, fields);
      if (partner.size !== remaining.length) {
        throw new PairingError('the round-pairing cannot be completed');
      }
      const upfloaters = lower.filter((p) => R.has(partner.get(p.id) as number));
      const bracket = [...residents, ...upfloaters];
      const pairs = this.pairBracket(bracket, A, explanation, fmt(s));
      finalPairs.push(...pairs);
      const done = new Set(bracket.map((p) => p.id));
      remaining = remaining.filter((p) => !done.has(p.id));
      explanation.push(
        `Bracket ${fmt(s)}: residents ${residents.map((p) => p.id).join(', ')}; upfloaters ${
          upfloaters.map((p) => `${p.id} (${fmt(p.score)})`).join(', ') || 'none'
        }; pairs ${pairs.map(([a, b]) => `${a.id}-${b.id}`).join(' ')}.`,
      );
    }

    const ordered = orderBoards(finalPairs, request);
    for (const o of ordered) {
      const r = DUBOV_COLOUR_RULE[o.rule];
      explanation.push(
        `Board ${o.board}: ${o.pair.white}-${o.pair.black}, colours: ${r.text} (Art. ${r.article}).`,
      );
    }
    return { pairs: ordered.map((o) => o.pair), byes, notes, explanation };
  }

  /** Articles 3.2.3-3.2.6: G1/G2, shifts, S1 by ARO and the first legal T2. */
  private pairBracket(
    bracket: readonly DutchPlayer[],
    aro: (p: DutchPlayer) => number,
    explanation: string[],
    label: string,
  ): [DutchPlayer, DutchPlayer][] {
    const byTpn = [...bracket].sort((a, b) => a.id - b.id);
    const allUnplayed = bracket.every((p) => p.colours.length === 0);
    let g1 = allUnplayed
      ? byTpn.slice(0, bracket.length / 2)
      : byTpn.filter((p) => pref(p) === 'w');
    let g2 = byTpn.filter((p) => !g1.includes(p));
    const sortFor = (group: 'g1' | 'g2', ps: readonly DutchPlayer[]): DutchPlayer[] =>
      middleFirst(
        [...ps].sort((a, b) => (group === 'g1' ? aro(a) - aro(b) || a.id - b.id : a.id - b.id)),
      );
    const ids = (ps: readonly DutchPlayer[]): Set<number> => new Set(ps.map((p) => p.id));
    const shifted: string[] = [];

    // 3.2.4.1 players of the smaller subgroup unavoidably paired together.
    const smallerIsG1 = g1.length <= g2.length;
    const small = smallerIsG1 ? g1 : g2;
    const probe = shiftOracle(bracket, ids(small), false, new Set(), new Set());
    let movedIn = new Set<number>();
    if (probe.within > 0) {
      const m = chooseShifters(
        bracket,
        ids(small),
        false,
        sortFor(smallerIsG1 ? 'g1' : 'g2', small),
        probe.within,
        new Set(),
      );
      movedIn = ids(m);
      if (smallerIsG1) {
        g1 = g1.filter((p) => !movedIn.has(p.id));
        g2 = [...g2, ...m];
      } else {
        g2 = g2.filter((p) => !movedIn.has(p.id));
        g1 = [...g1, ...m];
      }
      shifted.push(`3.2.4.1 moved ${m.map((p) => p.id).join(', ')}`);
    }
    // 3.2.4.2 equalise the subgroups.
    if (g1.length !== g2.length) {
      const largerIsG1 = g1.length > g2.length;
      const larger = largerIsG1 ? g1 : g2;
      const k = Math.abs(g1.length - g2.length) / 2;
      const poolPlayers = larger.filter((p) => !movedIn.has(p.id));
      const m = chooseShifters(
        bracket,
        ids(larger),
        true,
        sortFor(largerIsG1 ? 'g1' : 'g2', poolPlayers),
        k,
        movedIn,
      );
      const mi = ids(m);
      if (largerIsG1) {
        g1 = g1.filter((p) => !mi.has(p.id));
        g2 = [...g2, ...m];
      } else {
        g2 = g2.filter((p) => !mi.has(p.id));
        g1 = [...g1, ...m];
      }
      shifted.push(`3.2.4.2 moved ${m.map((p) => p.id).join(', ')}`);
    }

    // 3.2.5 S1 by ascending ARO, then TPN; 3.2.6 first legal transposition of G2.
    const s1 = [...g1].sort((a, b) => aro(a) - aro(b) || a.id - b.id);
    const t = [...g2].sort((a, b) => a.id - b.id);
    const i1 = new Map(s1.map((p, i) => [p.id, i]));
    const i2 = new Map(t.map((p, i) => [p.id, i]));
    const base = BigInt(t.length + 1);
    const partner = solve(
      [...s1, ...t],
      [
        { width: bitsFor(BigInt(bracket.length + 2)), value: () => 1n },
        {
          width: bitsFor(base ** BigInt(s1.length)) + 1n,
          value: (h, l) => {
            const a = i1.get(h.id) ?? i1.get(l.id);
            const b = i2.get(l.id) ?? i2.get(h.id);
            if (a === undefined || b === undefined) return 0n;
            return BigInt(t.length - b) * base ** BigInt(s1.length - 1 - a);
          },
        },
      ],
      (h, l) => (i1.has(h.id) && i2.has(l.id)) || (i2.has(h.id) && i1.has(l.id)),
    );
    if (partner.size !== bracket.length) {
      throw new PairingError(`bracket ${label}: no legal transposition (3.2.6)`);
    }
    const byId = new Map(bracket.map((p) => [p.id, p]));
    const pairs = s1.map((p) => [p, byId.get(partner.get(p.id) as number) as DutchPlayer] as const);
    explanation.push(
      `Bracket ${label}: S1 ${s1.map((p) => `${p.id} (ARO ${aro(p)})`).join(', ')}; G2 ${t
        .map((p) => p.id)
        .join(', ')}${shifted.length > 0 ? `; ${shifted.join('; ')}` : ''}.`,
    );
    return pairs.map(([a, b]) => [a, b]);
  }
}

/** Colours (Article 5) and board order (score, sum, TPN of the top player). */
function orderBoards(
  pairs: readonly [DutchPlayer, DutchPlayer][],
  request: PairingRequest,
): { board: number; pair: Pair; rule: ColourRule }[] {
  const byId = new Map(request.players.map((p) => [p.id, p]));
  const higherFirst = (a: DutchPlayer, b: DutchPlayer): readonly [DutchPlayer, DutchPlayer] =>
    a.score > b.score || (a.score === b.score && a.id < b.id) ? [a, b] : [b, a];
  const sorted = pairs
    .map(([a, b]) => higherFirst(a, b))
    .sort(
      ([a1, b1], [a2, b2]) =>
        a2.score - a1.score || a2.score + b2.score - (a1.score + b1.score) || a1.id - a2.id,
    );
  const state = (p: DutchPlayer): ColourState => {
    const cs = colourState(byId.get(p.id)?.history ?? []);
    // 1.6.4: no games means a mild preference for Black.
    return cs.preference === null ? { ...cs, preference: 'b', strength: 'mild' } : cs;
  };
  return sorted.map(([higher, lower], i) => {
    let c: Colour;
    let rule: ColourRule;
    if (higher.colours.length === 0 && lower.colours.length === 0) {
      // 5.2.1 both yet to play: TPN parity of the higher-ranked player.
      c = higher.id % 2 === 1 ? request.initialColour : request.initialColour === 'w' ? 'b' : 'w';
      rule = '5.2.5';
    } else {
      ({ colour: c, rule } = allocateColoursWithRule(
        state(higher),
        state(lower),
        higher.id,
        request.initialColour,
      ));
    }
    const pair =
      c === 'w' ? { white: higher.id, black: lower.id } : { white: lower.id, black: higher.id };
    return { board: i + 1, pair, rule };
  });
}
